/**
 * Transport-aware fetch for the CDK AppSync service layer (SEE-1395).
 *
 * A `fetch` that rejects (TypeError: "Load failed" / "Failed to fetch") means the
 * request never completed — dead socket, dropped connection, DNS failure. That is
 * fundamentally different from an HTTP error, where the server answered: transport
 * failures are safe to retry (nothing reached the backend in the overwhelming
 * majority of cases) and deserve different user-facing copy. Historically both
 * were surfaced as a bare Error, which is why receipt links silently vanished on
 * flaky mobile connections.
 */

export class NetworkTransportError extends Error {
  readonly attempts: number;

  constructor(message: string, attempts: number, cause?: unknown) {
    super(message);
    this.name = 'NetworkTransportError';
    this.attempts = attempts;
    // The project's TS lib types Error.cause as Error | undefined; fetch
    // rejections are always Error instances, so the narrowing is lossless.
    this.cause = cause instanceof Error ? cause : undefined;
  }
}

export function isNetworkTransportError(error: unknown): boolean {
  // instanceof alone is not enough: errors that cross a Redux thunk boundary
  // (dispatch(...).unwrap()) are flattened by miniSerializeError into a plain
  // {name, message} object, losing the class but keeping the name. Amplify
  // Storage (S3 uploads) surfaces a dead connection as an axios "Network Error"
  // instead — same physical failure, so classify it the same.
  if (error instanceof NetworkTransportError) {
    return true;
  }
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const { name, message } = error as { name?: unknown; message?: unknown };
  return name === 'NetworkTransportError' || message === 'Network Error';
}

const DEFAULT_RETRIES = 2; // total attempts = retries + 1
const DEFAULT_TIMEOUT_MS = 30_000;
const BACKOFF_MS = [500, 1500];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * `fetch` with per-attempt timeout and automatic retry on transport failure.
 *
 * Only transport-level failures (rejected fetch / timeout abort) are retried —
 * an HTTP response of any status resolves normally and is never retried here,
 * so server-side rejections stay single-shot. Exhausted retries throw
 * `NetworkTransportError` so callers can distinguish "connection dropped" from
 * "server said no".
 */
export async function fetchWithTransportRetry(
  input: RequestInfo,
  init: RequestInit,
  { retries = DEFAULT_RETRIES, timeoutMs = DEFAULT_TIMEOUT_MS }: { retries?: number; timeoutMs?: number } = {}
): Promise<Response> {
  let lastError: unknown;
  const attempts = retries + 1;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      // Overwrites any caller-provided init.signal — no caller passes one today.
      // If a caller ever needs its own cancellation, compose the two signals
      // (AbortSignal.any) instead of dropping this timeout.
      return await fetch(input, { ...init, signal: controller.signal });
    } catch (error) {
      lastError = error;
      if (attempt < attempts) {
        await delay(BACKOFF_MS[Math.min(attempt - 1, BACKOFF_MS.length - 1)]);
      }
    } finally {
      clearTimeout(timer);
    }
  }

  throw new NetworkTransportError(
    `Request failed in transit after ${attempts} attempts — the connection dropped before the server responded.`,
    attempts,
    lastError
  );
}
